import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { reportsApi, categoriesApi, transactionsApi } from '../lib/api';
import { FileDown, Calendar, Filter } from 'lucide-react';
import ExcelJS from 'exceljs';

const ACCOUNTS = ['BCA', 'Kas Internal', 'Kas Eksternal', 'Petty Cash'];
const RP_FMT = '"Rp"#,##0;[Red]-"Rp"#,##0';
const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } };
const HEADER_FONT = { bold: true, color: { argb: 'FFFFFFFF' } };
const SALDO_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCE6F1' } };
const TOTAL_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
const GREEN_FONT = { color: { argb: 'FF16A34A' } };
const RED_FONT = { color: { argb: 'FFDC2626' } };

const ReportsPage = () => {
  const [report, setReport] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    category_id: '',
    type: '',
    account: ''
  });

  useEffect(() => {
    fetchCategories();
    fetchReport();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await categoriesApi.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.category_id) params.category_id = filters.category_id;
      if (filters.type) params.type = filters.type;
      if (filters.account) params.account = filters.account;

      const response = await reportsApi.getReport(params);
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const styleHeaderRow = (ws) => {
    const row = ws.getRow(1);
    row.font = HEADER_FONT;
    row.fill = HEADER_FILL;
    ws.views = [{ state: 'frozen', ySplit: 1 }];
  };

  const exportToExcel = async () => {
    if (!report) return;
    try {
      setExporting(true);

      // Always export using the CURRENT filter values on screen, even if "Tampilkan" was not
      // clicked after the last change, so the filename/sheets never mismatch the active filters.
      const freshParams = {};
      if (filters.start_date) freshParams.start_date = filters.start_date;
      if (filters.end_date) freshParams.end_date = filters.end_date;
      if (filters.category_id) freshParams.category_id = filters.category_id;
      if (filters.type) freshParams.type = filters.type;
      if (filters.account) freshParams.account = filters.account;
      const freshReportRes = await reportsApi.getReport(freshParams);
      const freshReport = freshReportRes.data;

      // Fetch the full, unfiltered ledger so saldo berjalan (running balance) is always accurate,
      // regardless of the Kategori/Tipe filter currently applied on screen.
      const allRes = await transactionsApi.getAll({ limit: 10000 });
      const allTxns = allRes.data;

      const periodStart = filters.start_date || '0000-01-01';
      const periodEnd = filters.end_date || '9999-12-31';
      const selectedAccounts = filters.account ? [filters.account] : ACCOUNTS;

      const openingBalance = (acc) => allTxns
        .filter(t => (t.account || 'BCA') === acc && t.date < periodStart)
        .reduce((sum, t) => sum + (t.type === 'income' ? t.amount : -t.amount), 0);

      const wb = new ExcelJS.Workbook();
      wb.creator = 'HSEO Cash Flow System';
      wb.created = new Date();

      // ===== Sheet 1: Ringkasan =====
      const wsSummary = wb.addWorksheet('Ringkasan');
      wsSummary.columns = [
        { header: 'Akun', key: 'akun', width: 18 },
        { header: 'Saldo Awal', key: 'saldo_awal', width: 20 },
        { header: 'Total Kas Masuk', key: 'masuk', width: 20 },
        { header: 'Total Kas Keluar', key: 'keluar', width: 20 },
        { header: 'Saldo Akhir', key: 'saldo_akhir', width: 20 },
      ];
      styleHeaderRow(wsSummary);

      let grandOpening = 0, grandIn = 0, grandOut = 0;
      selectedAccounts.forEach(acc => {
        const opening = openingBalance(acc);
        const periodTx = allTxns.filter(t => (t.account || 'BCA') === acc && t.date >= periodStart && t.date <= periodEnd);
        const income = periodTx.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
        const expense = periodTx.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
        const closing = opening + income - expense;
        const row = wsSummary.addRow({ akun: acc, saldo_awal: opening, masuk: income, keluar: expense, saldo_akhir: closing });
        ['saldo_awal', 'masuk', 'keluar', 'saldo_akhir'].forEach(k => { row.getCell(k).numFmt = RP_FMT; });
        grandOpening += opening; grandIn += income; grandOut += expense;
      });
      const totalLabel = filters.account ? `TOTAL ${filters.account}` : 'TOTAL SEMUA AKUN';
      const totalRow = wsSummary.addRow({ akun: totalLabel, saldo_awal: grandOpening, masuk: grandIn, keluar: grandOut, saldo_akhir: grandOpening + grandIn - grandOut });
      totalRow.font = { bold: true };
      totalRow.fill = TOTAL_FILL;
      ['saldo_awal', 'masuk', 'keluar', 'saldo_akhir'].forEach(k => { totalRow.getCell(k).numFmt = RP_FMT; });

      wsSummary.addRow([]);
      const catTitleRow = wsSummary.addRow(['Ringkasan per Kategori (sesuai filter Kategori/Tipe saat ini)']);
      catTitleRow.font = { bold: true };
      const catHeaderRow = wsSummary.addRow(['Kategori', 'Kas Masuk', 'Kas Keluar']);
      catHeaderRow.font = HEADER_FONT;
      catHeaderRow.fill = HEADER_FILL;
      const catMap = {};
      freshReport.transactions.forEach(t => {
        if (!catMap[t.category_name]) catMap[t.category_name] = { income: 0, expense: 0 };
        catMap[t.category_name][t.type] += t.amount;
      });
      Object.entries(catMap).forEach(([cat, v]) => {
        const r = wsSummary.addRow([cat, v.income, v.expense]);
        r.getCell(2).numFmt = RP_FMT;
        r.getCell(3).numFmt = RP_FMT;
      });
      const catTotalRow = wsSummary.addRow(['TOTAL', freshReport.total_income, freshReport.total_expense]);
      catTotalRow.font = { bold: true };
      catTotalRow.fill = TOTAL_FILL;
      catTotalRow.getCell(2).numFmt = RP_FMT;
      catTotalRow.getCell(3).numFmt = RP_FMT;

      // ===== 1 sheet per akun (format mutasi/ledger: Tanggal, Deskripsi, Debet, Kredit, Saldo...) =====
      selectedAccounts.forEach(acc => {
        const ws = wb.addWorksheet(acc.substring(0, 31));
        ws.columns = [
          { header: 'Tanggal', key: 'tanggal', width: 12 },
          { header: 'Deskripsi', key: 'deskripsi', width: 42 },
          { header: 'Debet', key: 'debet', width: 18 },
          { header: 'Kredit', key: 'kredit', width: 18 },
          { header: 'Saldo', key: 'saldo', width: 18 },
          { header: 'Vendor', key: 'vendor', width: 24 },
          { header: 'Project', key: 'project', width: 24 },
          { header: 'Voucher', key: 'voucher', width: 14 },
        ];
        styleHeaderRow(ws);

        let running = openingBalance(acc);
        const openingRow = ws.addRow({ tanggal: periodStart, deskripsi: 'SALDO AWAL PERIODE', saldo: running, vendor: '', project: '', voucher: '' });
        openingRow.font = { bold: true };
        openingRow.fill = SALDO_FILL;
        openingRow.getCell('saldo').numFmt = RP_FMT;

        const periodTx = allTxns
          .filter(t => (t.account || 'BCA') === acc && t.date >= periodStart && t.date <= periodEnd)
          .sort((a, b) => a.date.localeCompare(b.date));

        periodTx.forEach(t => {
          running += (t.type === 'income' ? t.amount : -t.amount);
          const row = ws.addRow({
            tanggal: t.date,
            deskripsi: t.description,
            debet: t.type === 'income' ? t.amount : null,
            kredit: t.type === 'expense' ? t.amount : null,
            saldo: running,
            vendor: t.vendor_name || '',
            project: t.project_name || '',
            voucher: t.voucher || '',
          });
          row.getCell('debet').numFmt = RP_FMT;
          row.getCell('kredit').numFmt = RP_FMT;
          row.getCell('saldo').numFmt = RP_FMT;
          if (t.type === 'income') row.getCell('debet').font = GREEN_FONT;
          else row.getCell('kredit').font = RED_FONT;
        });

        const closingRow = ws.addRow({ tanggal: periodEnd, deskripsi: 'SALDO AKHIR PERIODE', saldo: running, vendor: '', project: '', voucher: '' });
        closingRow.font = { bold: true };
        closingRow.fill = SALDO_FILL;
        closingRow.getCell('saldo').numFmt = RP_FMT;
      });

      // ===== Sheet: Semua Transaksi (mengikuti filter Kategori/Tipe/Akun/Tanggal yang sedang aktif) =====
      const wsAll = wb.addWorksheet('Semua Transaksi');
      wsAll.columns = [
        { header: 'Tanggal', key: 'tanggal', width: 12 },
        { header: 'Akun', key: 'akun', width: 16 },
        { header: 'Deskripsi', key: 'deskripsi', width: 42 },
        { header: 'Kategori', key: 'kategori', width: 20 },
        { header: 'Tipe', key: 'tipe', width: 14 },
        { header: 'Debet', key: 'debet', width: 18 },
        { header: 'Kredit', key: 'kredit', width: 18 },
        { header: 'Vendor', key: 'vendor', width: 24 },
        { header: 'Project', key: 'project', width: 24 },
      ];
      styleHeaderRow(wsAll);

      [...freshReport.transactions].sort((a, b) => a.date.localeCompare(b.date)).forEach(t => {
        const row = wsAll.addRow({
          tanggal: t.date,
          akun: t.account || 'BCA',
          deskripsi: t.description,
          kategori: t.category_name,
          tipe: t.type === 'income' ? 'Kas Masuk' : 'Kas Keluar',
          debet: t.type === 'income' ? t.amount : null,
          kredit: t.type === 'expense' ? t.amount : null,
          vendor: t.vendor_name || '',
          project: t.project_name || '',
        });
        row.getCell('debet').numFmt = RP_FMT;
        row.getCell('kredit').numFmt = RP_FMT;
        if (t.type === 'income') row.getCell('debet').font = GREEN_FONT;
        else row.getCell('kredit').font = RED_FONT;
      });

      const totalAllRow = wsAll.addRow({ tanggal: '', akun: '', deskripsi: 'TOTAL', kategori: '', tipe: '', debet: freshReport.total_income, kredit: freshReport.total_expense, vendor: '', project: '' });
      totalAllRow.font = { bold: true };
      totalAllRow.fill = TOTAL_FILL;
      totalAllRow.getCell('debet').numFmt = RP_FMT;
      totalAllRow.getCell('kredit').numFmt = RP_FMT;

      const accountLabel = filters.account || 'Semua Akun';
      const filename = `Laporan Kas ${accountLabel}_${filters.start_date}_${filters.end_date}.xlsx`;

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Gagal export laporan: ' + (error.message || 'terjadi kesalahan'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Laporan</h1>
            <p className="text-gray-500 mt-1">Laporan keuangan dan arus kas</p>
          </div>
          {report && (
            <button
              data-testid="export-excel-button"
              onClick={exportToExcel}
              disabled={exporting}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <FileDown size={20} />
              {exporting ? 'Menyiapkan file...' : 'Export Excel'}
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dari Tanggal
              </label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sampai Tanggal
              </label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kategori
              </label>
              <select
                value={filters.category_id}
                onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Semua Kategori</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipe
              </label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Semua Tipe</option>
                <option value="income">Kas Masuk</option>
                <option value="expense">Kas Keluar</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Akun
              </label>
              <select
                data-testid="report-filter-account-select"
                value={filters.account}
                onChange={(e) => setFilters({ ...filters, account: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Semua Akun</option>
                {ACCOUNTS.map(acc => (
                  <option key={acc} value={acc}>{acc}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={fetchReport}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Filter size={20} />
                {loading ? 'Loading...' : 'Tampilkan'}
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {report && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <p className="text-sm text-gray-500 mb-1">Total Kas Masuk</p>
              <h3 className="text-2xl font-bold text-green-600">{formatCurrency(report.total_income)}</h3>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <p className="text-sm text-gray-500 mb-1">Total Kas Keluar</p>
              <h3 className="text-2xl font-bold text-red-600">{formatCurrency(report.total_expense)}</h3>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <p className="text-sm text-gray-500 mb-1">Net Cash Flow</p>
              <h3 className={`text-2xl font-bold ${report.net_cashflow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(report.net_cashflow)}
              </h3>
            </div>
          </div>
        )}

        {/* Transactions Table */}
        {report && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                Detail Transaksi ({report.transactions.length} transaksi)
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Periode: {report.period_start} s/d {report.period_end}
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deskripsi</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Akun</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipe</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Nominal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {report.transactions.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                        Tidak ada transaksi untuk periode ini
                      </td>
                    </tr>
                  ) : (
                    report.transactions.map((txn) => (
                      <tr key={txn.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">{txn.date}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{txn.description}</td>
                        <td className="px-6 py-4 text-sm">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                            {txn.account || 'BCA'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{txn.category_name}</td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            txn.type === 'income' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {txn.type === 'income' ? 'Kas Masuk' : 'Kas Keluar'}
                          </span>
                        </td>
                        <td className={`px-6 py-4 text-sm font-semibold text-right ${
                          txn.type === 'income' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {txn.type === 'income' ? '+' : '-'}{formatCurrency(txn.amount)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ReportsPage;
